'use client'

import { cva, type VariantProps } from 'class-variance-authority'
import dayjs from 'dayjs'
import Image from 'next/image'
import { useLayoutEffect, useState } from 'react'

import { TZ } from '@/legacy-src/constants/date.constants'
import { cn } from '@/legacy-src/lib/utils'

const SUPPORT_START_HOUR = 8
const SUPPORT_END_HOUR = 20
const WEEKEND_DAYS = [0, 6]

const isSupportOnline = async () => {
  const timezone = await import('dayjs/plugin/timezone')
  const utc = await import('dayjs/plugin/utc')

  dayjs.extend(timezone.default)
  dayjs.extend(utc.default)

  const isWeekday = !WEEKEND_DAYS.includes(dayjs().tz(TZ).day())
  const isWorkHours = dayjs().tz(TZ).hour() >= SUPPORT_START_HOUR && dayjs().tz(TZ).hour() < SUPPORT_END_HOUR
  return isWeekday && isWorkHours
}

const variants = cva(
  'absolute bottom-0 right-0 box-content animate-support-online-blink rounded-full border-solid border-sand-500 bg-petrol-500',
  {
    variants: {
      size: {
        small: 'h-[0.625rem] w-[0.625rem] border-4',
        big: 'h-[1rem] w-[1rem] border-6',
      },
    },
  },
)

type Size = NonNullable<VariantProps<typeof variants>['size']>

const mapSizeToImage: Record<
  Size,
  {
    width: number
    height: number
  }
> = {
  small: {
    width: 74,
    height: 85,
  },
  big: {
    width: 114,
    height: 131,
  },
}

export interface CustomerSupportImageProps {
  size: Size
}

export const CustomerSupportImage = ({ size }: CustomerSupportImageProps) => {
  const [online, setOnline] = useState(false)

  useLayoutEffect(() => {
    isSupportOnline().then(setOnline)
  }, [])

  const { width, height } = mapSizeToImage[size]

  const image = <Image sizes={`${width}px`} width={width} height={height} alt="" src={'/images/customer-support.png'} />

  if (!online) {
    return image
  }

  return (
    <div className="relative w-fit">
      {image}
      <span className={cn(variants({ size }))} />
    </div>
  )
}
